import { optionalTextQuery } from "../navigation/queryCodecs";
import { useNavigationViewport } from "components/scrapEyeDataDashboard/navigation/useNavigationViewport";
import { textQuery } from "components/scrapEyeDataDashboard/navigation/queryCodecs";
import { useNavigationQuery } from "components/scrapEyeDataDashboard/navigation/NavigationQueryProvider";
import { CircularProgress, MenuItem, Select, Stack } from "@mui/material";
import { useCallback, useMemo, useState } from "react";

import { useTenant } from "hooks/settings";
import {
  useGetTrainableConfigQuery,
  useGetModelCoverageConfigQuery,
} from "src/store/api/generatedApi";
import DateTimeControls from "../components/DateTimeControls";
import { TrainableSection } from "./TrainableSection";
import { ModelCoverageSection } from "./ModelCoverageSection";
import { JobFailuresSection } from "./JobFailuresSection";
import { DatasetSizeSection } from "./DatasetSizeSection";

export const TrainableAndCoverageTab = () => {
  const tenant = useTenant();
  const { pickerValue, handleRangeChange } = useNavigationViewport(
    "coverageRange",
    7
  );

  const { data: trainableConfig, isLoading: isLoadingTrainableConfig } =
    useGetTrainableConfigQuery({ tenantName: tenant });
  const { data: coverageConfig, isLoading: isLoadingCoverageConfig } =
    useGetModelCoverageConfigQuery({ tenantName: tenant });

  const availableLocations = useMemo(() => {
    const trainableLocs = trainableConfig?.available_locations ?? [];
    const coverageLocs = coverageConfig?.available_locations ?? [];
    return [...new Set([...trainableLocs, ...coverageLocs])];
  }, [trainableConfig, coverageConfig]);

  const [requestedLocation, setRequestedLocation] = useNavigationQuery(
    "coverageLocation",
    "",
    textQuery,
    "replace"
  );
  const defaultLocation =
    trainableConfig?.target_location ?? coverageConfig?.target_location ?? "";
  const selectedLocation = requestedLocation || defaultLocation;
  const [enabledAngleNames, setEnabledAngleNames] = useState<string[]>([]);
  const [requestedLabellingName, setSelectedLabellingName] = useNavigationQuery(
    "coverageModel",
    null,
    optionalTextQuery,
    "replace"
  );

  const availableModels =
    coverageConfig?.available_models[selectedLocation] ?? [];
  const selectedLabellingName =
    availableModels.find(
      (model) => model.labelling_name === requestedLabellingName
    )?.labelling_name ??
    availableModels[0]?.labelling_name ??
    null;

  const handleLocationChange = useCallback(
    (location: string) => {
      setRequestedLocation(location === defaultLocation ? "" : location);
      setEnabledAngleNames([]);
      setSelectedLabellingName(null);
    },
    [setRequestedLocation, defaultLocation, setSelectedLabellingName]
  );

  if (isLoadingTrainableConfig || isLoadingCoverageConfig) {
    return (
      <Stack alignItems="center" py={4}>
        <CircularProgress size={24} />
      </Stack>
    );
  }

  return (
    <Stack p={2} spacing={3}>
      <Stack direction="row" spacing={2} alignItems="center">
        <DateTimeControls
          pickerValue={pickerValue}
          onRangeChange={handleRangeChange}
        />
        {availableLocations.length > 1 ? (
          <Select
            size="small"
            value={selectedLocation}
            onChange={(event) => {
              handleLocationChange(event.target.value);
            }}
          >
            {availableLocations.map((location) => (
              <MenuItem key={location} value={location}>
                {location}
              </MenuItem>
            ))}
          </Select>
        ) : null}
      </Stack>

      <ModelCoverageSection
        selectedLocation={selectedLocation}
        onModelChange={(labellingName) => {
          setSelectedLabellingName(
            labellingName === availableModels[0]?.labelling_name
              ? null
              : labellingName
          );
        }}
        selectedLabellingName={selectedLabellingName}
      />
      <TrainableSection
        selectedLocation={selectedLocation}
        onEnabledAnglesChange={setEnabledAngleNames}
      >
        <DatasetSizeSection
          selectedLocation={selectedLocation}
          enabledAngleNames={enabledAngleNames}
        />
      </TrainableSection>
      <JobFailuresSection
        selectedLocation={selectedLocation}
        enabledAngleNames={enabledAngleNames}
        selectedLabellingName={selectedLabellingName}
      />
    </Stack>
  );
};
